/* eslint-disable no-console */
import { useCallback, useEffect, useState } from 'react';
import { useWindowEvent } from '../use-window-event';

export type StorageType = 'localStorage' | 'sessionStorage';

/**
 * Interface for storage properties
 * @author Karim Bakkali
 * @template T The type of the stored value
 */
export interface StorageProperties<T> {
  /** Storage key */
  key: string;

  /** Default value that will be set if value is not found in storage */
  defaultValue?: T;

  /** If set to true, value will be updated in useEffect after mount. Default value is true. */
  getInitialValueInEffect?: boolean;

  /** Function to serialize value into string to be save in storage */
  serialize?: (value: T) => string;

  /** Function to deserialize string value from storage to value */
  deserialize?: (value: string | undefined) => T;
}

/**
 * Serializes a value to JSON
 * @author Karim Bakkali
 * @template T The type of the value to serialize
 * @param {T} value The value to serialize
 * @param {string} [hookName='use-local-storage'] The name of the hook (for error messages)
 * @returns {string} The serialized value
 * @throws {Error} If serialization fails
 */
function serializeJSON<T>(
  value: T,
  hookName: string = 'use-local-storage'
): string {
  try {
    return JSON.stringify(value);
  } catch (error) {
    throw new Error(`@atarim/hooks ${hookName}: Failed to serialize the value`);
  }
}

/**
 * Deserializes a JSON string
 * @author Karim Bakkali
 * @param {string | undefined} value The value to deserialize
 * @returns {any} The deserialized value
 */
function deserializeJSON(value: string | undefined): any {
  try {
    return value && JSON.parse(value);
  } catch {
    return value;
  }
}

type StorageHandler = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};
/**
 * Creates a storage handler for the specified storage type
 * @author Karim Bakkali
 * @param {StorageType} type The type of storage ('localStorage' or 'sessionStorage')
 * @returns {StorageHandler} The storage handler
 */
function createStorageHandler(type: StorageType): StorageHandler {
  const getItem = (key: string) => {
    try {
      return window[type].getItem(key);
    } catch (error) {
      console.warn(
        'use-local-storage: Failed to get value from storage, localStorage is blocked'
      );
      return null;
    }
  };

  const setItem = (key: string, value: string) => {
    try {
      window[type].setItem(key, value);
    } catch (error) {
      console.warn(
        'use-local-storage: Failed to set value to storage, localStorage is blocked'
      );
    }
  };

  const removeItem = (key: string) => {
    try {
      window[type].removeItem(key);
    } catch (error) {
      console.warn(
        'use-local-storage: Failed to remove value from storage, localStorage is blocked'
      );
    }
  };

  return { getItem, setItem, removeItem };
}

/**
 * Creates a custom storage hook
 * @author Karim Bakkali
 * @template T The type of the stored value
 * @param {StorageType} type The type of storage ('localStorage' or 'sessionStorage')
 * @param {string} hookName The name of the hook
 * @returns {Function} A custom hook for managing storage
 */
export function createStorage<T>(
  type: StorageType,
  hookName: string
): Function {
  const eventName =
    type === 'localStorage' ? 'atarim-local-storage' : 'atarim-session-storage';
  const { getItem, setItem, removeItem } = createStorageHandler(type);

  return function useStorage({
    key,
    defaultValue,
    getInitialValueInEffect = true,
    deserialize = deserializeJSON,
    serialize = (value: T) => serializeJSON(value, hookName),
  }: StorageProperties<T>) {
    const readStorageValue = useCallback(
      (skipStorage?: boolean): T => {
        let storageBlockedOrSkipped;

        try {
          storageBlockedOrSkipped =
            typeof window === 'undefined' ||
            !(type in window) ||
            window[type] === null ||
            !!skipStorage;
        } catch (_e) {
          storageBlockedOrSkipped = true;
        }

        if (storageBlockedOrSkipped) {
          return defaultValue as T;
        }

        const storageValue = getItem(key);
        return storageValue !== null
          ? deserialize(storageValue)
          : (defaultValue as T);
      },
      [key, defaultValue]
    );

    const [value, setValue] = useState<T>(
      readStorageValue(getInitialValueInEffect)
    );

    const setStorageValue = useCallback(
      (val: T | ((prevState: T) => T)) => {
        if (val instanceof Function) {
          setValue((current) => {
            const result = val(current);
            setItem(key, serialize(result));
            window.dispatchEvent(
              new CustomEvent(eventName, {
                detail: { key, value: val(current) },
              })
            );
            return result;
          });
        } else {
          setItem(key, serialize(val));
          window.dispatchEvent(
            new CustomEvent(eventName, { detail: { key, value: val } })
          );
          setValue(val);
        }
      },
      [key]
    );

    const removeStorageValue = useCallback(() => {
      removeItem(key);
      window.dispatchEvent(
        new CustomEvent(eventName, { detail: { key, value: defaultValue } })
      );
    }, []);

    useWindowEvent('storage', (event) => {
      if (event.storageArea === window[type] && event.key === key) {
        setValue(deserialize(event.newValue ?? undefined));
      }
    });

    useWindowEvent(eventName, (event) => {
      if (event.detail.key === key) {
        setValue(event.detail.value);
      }
    });

    useEffect(() => {
      if (defaultValue !== undefined && value === undefined) {
        setStorageValue(defaultValue);
      }
    }, [defaultValue, value, setStorageValue]);

    useEffect(() => {
      const val = readStorageValue();
      val !== undefined && setStorageValue(val);
    }, []);

    return [
      value === undefined ? defaultValue : value,
      setStorageValue,
      removeStorageValue,
    ] as [T, (val: T | ((prevState: T) => T)) => void, () => void];
  };
}

/**
 * Creates a function to read a value from storage
 * @author Karim Bakkali
 * @param {StorageType} type The type of storage ('localStorage' or 'sessionStorage')
 * @returns {Function} A function to read a value from storage
 */
export function readValue(type: StorageType): Function {
  const { getItem } = createStorageHandler(type);

  return function read<T>({
    key,
    defaultValue,
    deserialize = deserializeJSON,
  }: StorageProperties<T>) {
    let storageBlockedOrSkipped;

    try {
      storageBlockedOrSkipped =
        typeof window === 'undefined' ||
        !(type in window) ||
        window[type] === null;
    } catch (_e) {
      storageBlockedOrSkipped = true;
    }

    if (storageBlockedOrSkipped) {
      return defaultValue as T;
    }

    const storageValue = getItem(key);
    return storageValue !== null
      ? deserialize(storageValue)
      : (defaultValue as T);
  };
}
